import '@/components/tooltip/styles/tooltip.scss';

import { memo } from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import classNames from 'classnames';
import { v4 as UUID4 } from 'uuid';

import type { ComponentProps, CSSProperties, ReactNode } from 'react';

export enum TooltipColors {
	DARK = 'dark',
	LIGHT = 'light',
	PURPLE = 'purple',
	SEA = 'sea',
	ORANGE = 'orange',
	YELLOW = 'yellow',
}

export enum TooltipSizes {
	SMALL = 'small',
	MEDIUM = 'medium',
	LARGE = 'large',
}

type ReactTooltipProps = ComponentProps<typeof ReactTooltip>;

type TooltipProps = Omit<ReactTooltipProps, 'content' | 'anchorSelect' | 'children'> & {
	children?: ReactNode;
	title?: ReactNode;
	width?: CSSProperties['width'];
	anchor?: string;
	bouncing?: boolean;
	color?: TooltipColors;
	size?: TooltipSizes;
};

const DEFAULT_PLACE: TooltipProps['place'] = 'top';

const Tooltip = ({
	children,
	place = DEFAULT_PLACE,
	title,
	hidden = false,
	width = 'auto',
	noArrow = false,
	anchor,
	render: customRender,
	className,
	color = TooltipColors.DARK,
	size = TooltipSizes.MEDIUM,
	bouncing,
	...rest
}: TooltipProps) => {
	const anchorSelect = anchor ?? `tooltip-anchor-${UUID4()}`;

	if (!title) {
		return <>{children}</>;
	}

	return (
		<>
			{children && <div id={anchorSelect}>{children}</div>}
			<ReactTooltip
				{...rest}
				place={place}
				noArrow={noArrow}
				className={classNames(
					'tooltip-container',
					`tooltip-container--${color}`,
					`tooltip-container--${size}`,
					bouncing && 'tooltip-container--bouncing',
					className,
				)}
				render={
					customRender ??
					(() => (
						<div className="tooltip" style={{ width }}>
							<span
								className="tooltip-title"
								dangerouslySetInnerHTML={{
									__html: title,
								}}
							/>
						</div>
					))
				}
				anchorSelect={`#${anchorSelect}`}
				hidden={hidden}
			/>
		</>
	);
};

export default memo(Tooltip);
